import type { AppDictionary } from './dictionaries';
import { getDictionary } from './dictionaries';
import type { LandingContent } from './landingContent';
import { getLandingContent } from './landingContent';
import type { SupportedLocale } from './logic';
import { FULL_I18N_SCOPE, type I18nPayloadScope, LOGIN_I18N_SCOPE } from './payloadScope';

export type I18nPayload = {
    locale: SupportedLocale;
    dictionary: AppDictionary;
    landingContent: LandingContent;
};

type LoginCommonDictionary = Pick<
    AppDictionary['common'],
    'close' | 'copied' | 'currentBrowser' | 'done' | 'loading' | 'login' | 'moreActions' | 'thisBrowser'
>;

export type LoginDictionary = Pick<AppDictionary, 'auth' | 'languages' | 'localeLabel' | 'vpnPlusShareCode'> & {
    common: LoginCommonDictionary;
};

export type LoginI18nPayload = {
    scope: typeof LOGIN_I18N_SCOPE;
    locale: SupportedLocale;
    dictionary: LoginDictionary;
};

export type RuntimeI18nPayload = I18nPayload | LoginI18nPayload;

export const getI18nPayload = (locale: SupportedLocale): I18nPayload => ({
    locale,
    dictionary: getDictionary(locale),
    landingContent: getLandingContent(locale),
});

export const getLoginI18nPayload = (locale: SupportedLocale): LoginI18nPayload => {
    const dictionary = getDictionary(locale);

    return {
        scope: LOGIN_I18N_SCOPE,
        locale,
        dictionary: {
            localeLabel: dictionary.localeLabel,
            languages: dictionary.languages,
            common: {
                close: dictionary.common.close,
                copied: dictionary.common.copied,
                currentBrowser: dictionary.common.currentBrowser,
                done: dictionary.common.done,
                loading: dictionary.common.loading,
                login: dictionary.common.login,
                moreActions: dictionary.common.moreActions,
                thisBrowser: dictionary.common.thisBrowser,
            },
            auth: dictionary.auth,
            vpnPlusShareCode: dictionary.vpnPlusShareCode,
        },
    };
};

export const getScopedI18nPayload = (locale: SupportedLocale, scope: I18nPayloadScope): RuntimeI18nPayload =>
    scope === LOGIN_I18N_SCOPE ? getLoginI18nPayload(locale) : getI18nPayload(locale);

export const getRuntimeI18nPayloadScope = (payload: RuntimeI18nPayload): I18nPayloadScope =>
    'scope' in payload && payload.scope === LOGIN_I18N_SCOPE ? LOGIN_I18N_SCOPE : FULL_I18N_SCOPE;
